import { useEffect, useState } from "react";
import api, { resolveAssetUrl } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";
import { refreshSettings } from "@/lib/useSettings";
import { Trash2, Plus, ShieldCheck, ArrowUp, ArrowDown, Bike } from "lucide-react";

const TABS = [
  { key: "settings", label: "Ustawienia" },
  { key: "users", label: "Członkowie" },
  { key: "announcements", label: "Aktualności" },
  { key: "events", label: "Wydarzenia" },
  { key: "history", label: "Historia" },
  { key: "docs", label: "Regulamin" },
  { key: "gallery", label: "Galeria" },
  { key: "videos", label: "Filmy" },
  { key: "partners", label: "Nasi znajomi" },
  { key: "in_memory", label: "In Memory" },
  { key: "formations", label: "Szyk jazdy" },
  { key: "messages", label: "Wiadomości" },
];

export default function Admin() {
  const [tab, setTab] = useState("settings");

  return (
    <div className="max-w-6xl mx-auto px-5 py-12" data-testid="admin-page">
      <SectionHeader title="Panel Admina" />
      <div className="flex flex-wrap gap-2 mt-8 border-b" style={{ borderColor: "var(--sr-border)" }}>
        {TABS.map((t) => (
          <button key={t.key} className={`tab ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="mt-8">
        {tab === "settings" && <SettingsTab />}
        {tab === "users" && <UsersTab />}
        {tab === "announcements" && <AnnouncementsTab />}
        {tab === "events" && <EventsTab />}
        {tab === "history" && <HistoryTab />}
        {tab === "docs" && <DocsTab />}
        {tab === "gallery" && <GalleryTab />}
        {tab === "videos" && <VideosTab />}
        {tab === "partners" && <PartnersTab />}
        {tab === "in_memory" && <InMemoryTab />}
        {tab === "formations" && <FormationsTab />}
        {tab === "messages" && <MessagesTab />}
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", name: "", bio: "", motorcycle: "", avatar_url: "" });
  const [err, setErr] = useState("");
  const [pwdModal, setPwdModal] = useState(null); // {id, username}
  const [newPwd, setNewPwd] = useState("");
  const [pwdStatus, setPwdStatus] = useState("");
  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/users", form);
      setForm({ username: "", password: "", name: "", bio: "", motorcycle: "", avatar_url: "" });
      setShowForm(false);
      load();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Błąd");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Usunąć członka? Po usunięciu nie będzie mógł się zalogować.")) return;
    await api.delete(`/users/${id}`);
    load();
  };

  const changePwd = async (e) => {
    e.preventDefault();
    setPwdStatus("");
    if (newPwd.length < 6) { setPwdStatus("Hasło musi mieć min. 6 znaków."); return; }
    try {
      await api.put(`/users/${pwdModal.id}`, { password: newPwd });
      setPwdStatus("OK");
      setNewPwd("");
      setTimeout(() => { setPwdModal(null); setPwdStatus(""); }, 1200);
    } catch (e2) {
      setPwdStatus("Błąd: " + (e2.response?.data?.detail || e2.message));
    }
  };

  return (
    <div data-testid="admin-users">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-display text-xl text-[var(--sr-cream)]">Lista członków ({users.length})</h3>
        <button className="btn-solid flex items-center gap-2" onClick={() => setShowForm(!showForm)} data-testid="add-user-btn">
          <Plus size={16} /> Dodaj członka
        </button>
      </div>
      {showForm && (
        <form onSubmit={submit} className="sr-card p-5 mb-5 grid sm:grid-cols-2 gap-4" data-testid="add-user-form">
          <div><label className="sr-label">Login</label><input className="sr-input" required value={form.username} onChange={(e) => setForm({...form, username: e.target.value})} data-testid="new-username" /></div>
          <div><label className="sr-label">Hasło</label><input className="sr-input" type="password" required value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} data-testid="new-password" /></div>
          <div><label className="sr-label">Imię i nazwisko</label><input className="sr-input" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></div>
          <div><label className="sr-label">Motocykl</label><input className="sr-input" value={form.motorcycle} onChange={(e) => setForm({...form, motorcycle: e.target.value})} /></div>
          <div className="sm:col-span-2"><label className="sr-label">Bio</label><input className="sr-input" value={form.bio} onChange={(e) => setForm({...form, bio: e.target.value})} /></div>
          {err && <p className="text-red-400 text-sm sm:col-span-2">{String(err)}</p>}
          <div className="sm:col-span-2"><button className="btn-fire" type="submit">Dodaj</button></div>
        </form>
      )}
      <div className="overflow-x-auto sr-card p-2">
        <table className="sr-table">
          <thead>
            <tr><th>Login</th><th>Imię</th><th>Pozycja</th><th>Funkcja</th><th>Motocykl</th><th></th></tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const updateName = async (val) => {
                const trimmed = val.trim();
                if (!trimmed || trimmed === (u.name || "")) return;
                await api.put(`/users/${u.id}`, { name: trimmed });
                load();
              };
              const updatePosition = async (pos) => {
                await api.put(`/users/${u.id}`, { position: pos });
                load();
              };
              const updateFunction = async (val) => {
                await api.put(`/users/${u.id}`, { function_title: val });
                load();
              };
              return (
                <tr key={u.id} data-testid={`row-user-${u.username}`}>
                  <td>{u.username}</td>
                  <td>
                    <input
                      type="text"
                      defaultValue={u.name || ""}
                      onBlur={(e) => updateName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                      placeholder="Imię / nazwa"
                      className="sr-input"
                      style={{ minWidth: 150, padding: "4px 8px" }}
                      data-testid={`name-user-${u.username}`}
                    />
                  </td>
                  <td>
                    <select
                      value={u.position || (u.role === "admin" ? "prezydent" : "member")}
                      onChange={(e) => updatePosition(e.target.value)}
                      className="sr-input"
                      style={{ minWidth: 120, padding: "4px 8px" }}
                      data-testid={`pos-user-${u.username}`}
                    >
                      <option value="prezydent">Prezydent</option>
                      <option value="zarzad">Zarząd</option>
                      <option value="member">Member</option>
                      <option value="prospect">Prospect</option>
                    </select>
                  </td>
                  <td>
                    <input
                      type="text"
                      defaultValue={u.function_title || ""}
                      onBlur={(e) => {
                        if (e.target.value !== (u.function_title || "")) updateFunction(e.target.value);
                      }}
                      placeholder="np. Wiceprezes"
                      className="sr-input"
                      style={{ minWidth: 130, padding: "4px 8px" }}
                      data-testid={`fn-user-${u.username}`}
                    />
                  </td>
                  <td>{u.motorcycle}</td>
                  <td className="text-right">
                    <div className="inline-flex gap-2 items-center">
                      <button
                        className="text-xs font-nav uppercase tracking-widest px-2 py-1 hover:text-[var(--sr-fire)]"
                        style={{ color: "var(--sr-cream-soft)", border: "1px solid var(--sr-border)" }}
                        onClick={() => { setPwdModal({ id: u.id, username: u.username, name: u.name }); setNewPwd(""); setPwdStatus(""); }}
                        data-testid={`pwd-user-${u.username}`}
                      >
                        Zmień hasło
                      </button>
                      {u.role !== "admin" && (
                        <button className="text-[var(--sr-muted)] hover:text-red-400" onClick={() => remove(u.id)} data-testid={`del-user-${u.username}`}>
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pwdModal && (
        <div className="modal-bg" onClick={() => setPwdModal(null)} data-testid="pwd-modal">
          <form onSubmit={changePwd} className="sr-card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl text-[var(--sr-cream)] mb-4">
              Zmień hasło: <span className="text-[var(--sr-fire-2)]">{pwdModal.name}</span>
            </h3>
            <label className="sr-label">Nowe hasło (min. 6 znaków)</label>
            <input type="password" autoFocus minLength={6} required className="sr-input" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} data-testid="pwd-modal-input" />
            {pwdStatus && (
              <p className="text-sm mt-3" style={{ color: pwdStatus === "OK" ? "#86efac" : "#fca5a5" }}>
                {pwdStatus === "OK" ? "Hasło zmienione." : pwdStatus}
              </p>
            )}
            <div className="mt-5 flex gap-2 justify-end">
              <button type="button" className="btn-ghost" onClick={() => setPwdModal(null)}>Anuluj</button>
              <button type="submit" className="btn-fire" data-testid="pwd-modal-submit">Zapisz</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function AnnouncementsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", body: "" });
  const load = () => api.get("/announcements").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    await api.post("/announcements", form);
    setForm({ title: "", body: "" });
    load();
  };
  const remove = async (id) => { if (window.confirm("Usunąć?")) { await api.delete(`/announcements/${id}`); load(); } };
  return (
    <div data-testid="admin-announcements">
      <form onSubmit={submit} className="sr-card p-5 mb-5">
        <label className="sr-label">Tytuł</label>
        <input className="sr-input mb-3" required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} />
        <label className="sr-label">Treść</label>
        <textarea className="sr-input" rows={3} required value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} />
        <button type="submit" className="btn-fire mt-3" data-testid="add-announcement-btn">Dodaj aktualność</button>
      </form>
      <div className="space-y-3">
        {items.map((a) => (
          <div key={a.id} className="sr-card p-4 flex justify-between gap-3">
            <div>
              <p className="font-display text-[var(--sr-fire-2)]">{a.title}</p>
              <p className="text-sm" style={{ color: "var(--sr-cream-soft)" }}>{a.body}</p>
            </div>
            <button className="text-[var(--sr-muted)] hover:text-red-400" onClick={() => remove(a.id)}><Trash2 size={16}/></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function EventsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", location: "", date: "", image_url: "" });
  const load = () => api.get("/events").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    const date = new Date(form.date).toISOString();
    await api.post("/events", { ...form, date });
    setForm({ title: "", description: "", location: "", date: "", image_url: "" });
    load();
  };
  const remove = async (id) => { if (window.confirm("Usunąć?")) { await api.delete(`/events/${id}`); load(); } };
  return (
    <div data-testid="admin-events">
      <form onSubmit={submit} className="sr-card p-5 mb-5 grid sm:grid-cols-2 gap-3">
        <div><label className="sr-label">Tytuł</label><input className="sr-input" required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} /></div>
        <div><label className="sr-label">Lokalizacja</label><input className="sr-input" required value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} /></div>
        <div><label className="sr-label">Data</label><input className="sr-input" type="datetime-local" required value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} /></div>
        <div><label className="sr-label">URL zdjęcia (opcjonalnie)</label><input className="sr-input" value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} /></div>
        <div className="sm:col-span-2"><label className="sr-label">Opis</label><textarea className="sr-input" rows={3} required value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} /></div>
        <div className="sm:col-span-2"><button type="submit" className="btn-fire" data-testid="add-event-btn">Dodaj wydarzenie</button></div>
      </form>
      <div className="space-y-3">
        {items.map((ev) => (
          <div key={ev.id} className="sr-card p-4 flex justify-between gap-3">
            <div>
              <p className="font-display text-[var(--sr-fire-2)]">{ev.title}</p>
              <p className="text-xs font-nav uppercase tracking-widest" style={{ color: "var(--sr-muted)" }}>
                {new Date(ev.date).toLocaleString("pl-PL")} • {ev.location} • {ev.attendees?.length || 0} zapisanych
              </p>
            </div>
            <button className="text-[var(--sr-muted)] hover:text-red-400" onClick={() => remove(ev.id)}><Trash2 size={16}/></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function GalleryTab() {
  const [albums, setAlbums] = useState([]);
  const [openAlbum, setOpenAlbum] = useState(null); // id
  const [photos, setPhotos] = useState([]);
  const [albumForm, setAlbumForm] = useState({ title: "", category: "wyprawy", is_public: true, description: "" });
  const [editingAlbumId, setEditingAlbumId] = useState(null);
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoCaption, setPhotoCaption] = useState("");
  const [uploading, setUploading] = useState(false);

  const loadAlbums = () => api.get("/albums").then((r) => setAlbums(r.data || []));
  const loadPhotos = (aid) => api.get(`/gallery?album_id=${aid}`).then((r) => setPhotos(r.data || []));

  useEffect(() => { loadAlbums(); }, []);
  useEffect(() => { if (openAlbum) loadPhotos(openAlbum); }, [openAlbum]);

  const saveAlbum = async (e) => {
    e.preventDefault();
    if (!albumForm.title.trim()) { alert("Podaj nazwę albumu"); return; }
    if (editingAlbumId) {
      await api.put(`/albums/${editingAlbumId}`, albumForm);
    } else {
      await api.post("/albums", albumForm);
    }
    setAlbumForm({ title: "", category: "wyprawy", is_public: true, description: "" });
    setEditingAlbumId(null);
    loadAlbums();
  };

  const editAlbum = (a) => {
    setEditingAlbumId(a.id);
    setAlbumForm({ title: a.title, category: a.category, is_public: a.is_public, description: a.description || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteAlbum = async (a) => {
    if (!window.confirm(`Usunąć album "${a.title}" i WSZYSTKIE ${a.photo_count} zdjęć w nim?`)) return;
    await api.delete(`/albums/${a.id}`);
    loadAlbums();
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/gallery", { image_url: data.url, caption: photoCaption, album_id: openAlbum });
      setPhotoCaption("");
      loadPhotos(openAlbum);
      loadAlbums();
    } catch (err) {
      alert("Błąd uploadu: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const addPhotoByUrl = async () => {
    if (!photoUrl.trim()) return;
    await api.post("/gallery", { image_url: photoUrl, caption: photoCaption, album_id: openAlbum });
    setPhotoUrl("");
    setPhotoCaption("");
    loadPhotos(openAlbum);
    loadAlbums();
  };

  const deletePhoto = async (id) => {
    if (!window.confirm("Usunąć zdjęcie?")) return;
    await api.delete(`/gallery/${id}`);
    loadPhotos(openAlbum);
    loadAlbums();
  };

  const imgUrl = (u) => u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`;

  // ========= INSIDE ALBUM =========
  if (openAlbum) {
    const album = albums.find((x) => x.id === openAlbum);
    return (
      <div data-testid="admin-album-detail">
        <button className="btn-ghost mb-4" onClick={() => setOpenAlbum(null)}>← Wróć do listy albumów</button>
        <h3 className="font-display text-xl text-[var(--sr-cream)]">
          {album?.title} <span className="text-sm" style={{ color: "var(--sr-muted)" }}>({photos.length} zdjęć)</span>
        </h3>

        <div className="sr-card p-5 my-5 grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="sr-label">Dodaj zdjęcie z dysku</label>
            <input type="file" accept="image/*" onChange={uploadPhoto} className="sr-input" data-testid="album-upload" />
            {uploading && <p className="text-xs mt-2" style={{ color: "var(--sr-fire)" }}>Wgrywanie...</p>}
          </div>
          <div className="sm:col-span-2">
            <label className="sr-label">…lub wklej URL zdjęcia</label>
            <div className="flex gap-2">
              <input className="sr-input flex-1" value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)} placeholder="https://..." />
              <button className="btn-solid" onClick={addPhotoByUrl}>Dodaj</button>
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="sr-label">Podpis (opcjonalny)</label>
            <input className="sr-input" value={photoCaption} onChange={(e) => setPhotoCaption(e.target.value)} />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {photos.map((g) => (
            <div key={g.id} className="sr-card p-2 relative">
              <div className="gallery-img" style={{ aspectRatio: "1/1" }}><img src={imgUrl(g.image_url)} alt={g.caption} /></div>
              {g.caption && <p className="text-xs mt-2 text-center" style={{ color: "var(--sr-muted)" }}>{g.caption}</p>}
              <button className="absolute top-3 right-3 bg-black/70 p-1 text-red-400 hover:text-red-200" onClick={() => deletePhoto(g.id)} data-testid={`del-photo-${g.id}`}>
                <Trash2 size={14}/>
              </button>
            </div>
          ))}
          {photos.length === 0 && <p className="col-span-full text-center" style={{ color: "var(--sr-muted)" }}>Ten album jest pusty. Dodaj pierwsze zdjęcie.</p>}
        </div>
      </div>
    );
  }

  // ========= ALBUMS LIST =========
  return (
    <div data-testid="admin-gallery">
      <form onSubmit={saveAlbum} className="sr-card p-5 mb-5 grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="sr-label">Nazwa albumu</label>
          <input className="sr-input" required value={albumForm.title} onChange={(e) => setAlbumForm({...albumForm, title: e.target.value})} placeholder="np. Zlot w Bieszczadach 2024" data-testid="album-title" />
        </div>
        <div>
          <label className="sr-label">Kategoria</label>
          <select className="sr-input" value={albumForm.category} onChange={(e) => setAlbumForm({...albumForm, category: e.target.value})} data-testid="album-category">
            <option value="zloty">Zloty</option>
            <option value="wyprawy">Wyprawy</option>
            <option value="klub">Klubowe (tylko dla członków)</option>
            <option value="inne">Inne</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <input id="album-public" type="checkbox" checked={albumForm.is_public} onChange={(e) => setAlbumForm({...albumForm, is_public: e.target.checked})} />
          <label htmlFor="album-public" className="font-nav text-sm uppercase tracking-widest" style={{ color: "var(--sr-cream-soft)" }}>Publiczny</label>
        </div>
        <div className="sm:col-span-2">
          <label className="sr-label">Opis (opcjonalny)</label>
          <input className="sr-input" value={albumForm.description} onChange={(e) => setAlbumForm({...albumForm, description: e.target.value})} />
        </div>
        <div className="sm:col-span-2 flex gap-2">
          <button type="submit" className="btn-fire" data-testid="save-album-btn">
            {editingAlbumId ? "Zapisz zmiany" : "Utwórz album"}
          </button>
          {editingAlbumId && (
            <button type="button" className="btn-ghost" onClick={() => { setEditingAlbumId(null); setAlbumForm({ title: "", category: "wyprawy", is_public: true, description: "" }); }}>
              Anuluj
            </button>
          )}
        </div>
      </form>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {albums.map((a) => (
          <div key={a.id} className="sr-card p-3 flex gap-3 items-start">
            <div className="flex-shrink-0" style={{ width: 100, height: 80, background: "rgba(0,0,0,0.4)" }}>
              {a.cover_url ? (
                <img src={imgUrl(a.cover_url)} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[10px] font-nav uppercase" style={{ color: "var(--sr-muted)" }}>brak okładki</div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display text-[var(--sr-fire-2)] truncate">{a.title}</p>
              <p className="text-xs font-nav uppercase tracking-widest" style={{ color: "var(--sr-muted)" }}>
                {a.category} · {a.photo_count} zdjęć · {a.is_public ? "publiczny" : "klubowy"}
              </p>
              <div className="mt-2 flex gap-2 flex-wrap">
                <button className="btn-solid text-xs" onClick={() => setOpenAlbum(a.id)} data-testid={`open-album-${a.id}`}>Otwórz / dodaj zdjęcia</button>
                <button className="btn-ghost text-xs" onClick={() => editAlbum(a)}>Edytuj</button>
                <button className="text-[var(--sr-muted)] hover:text-red-400" onClick={() => deleteAlbum(a)}><Trash2 size={15}/></button>
              </div>
            </div>
          </div>
        ))}
        {albums.length === 0 && <p className="col-span-full text-center" style={{ color: "var(--sr-muted)" }}>Brak albumów. Utwórz pierwszy powyżej.</p>}
      </div>
    </div>
  );
}

function DocsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", order: 0 });
  const [editingId, setEditingId] = useState(null);
  const load = () => api.get("/docs").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (editingId) {
      await api.put(`/docs/${editingId}`, form);
      setEditingId(null);
    } else {
      await api.post("/docs", form);
    }
    setForm({ title: "", body: "", order: 0 });
    load();
  };
  const edit = (d) => { setForm({ title: d.title, body: d.body, order: d.order || 0 }); setEditingId(d.id); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const remove = async (id) => { if (window.confirm("Usunąć dokument?")) { await api.delete(`/docs/${id}`); load(); } };
  return (
    <div data-testid="admin-docs">
      <form onSubmit={submit} className="sr-card p-5 mb-5 grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2"><label className="sr-label">Tytuł</label><input className="sr-input" required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="np. Regulamin klubu" data-testid="new-doc-title" /></div>
        <div><label className="sr-label">Kolejność (mniejsza = wyżej)</label><input type="number" className="sr-input" value={form.order} onChange={(e) => setForm({...form, order: Number(e.target.value)})} /></div>
        <div className="sm:col-span-2"><label className="sr-label">Treść</label><textarea className="sr-input" rows={10} required value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} placeholder="Treść dokumentu (możesz użyć linii pustych do akapitów)" data-testid="new-doc-body" /></div>
        <div className="sm:col-span-2 flex gap-2">
          <button type="submit" className="btn-fire" data-testid="save-doc-btn">{editingId ? "Zapisz zmiany" : "Dodaj dokument"}</button>
          {editingId && <button type="button" className="btn-ghost" onClick={() => { setEditingId(null); setForm({ title: "", body: "", order: 0 }); }}>Anuluj</button>}
        </div>
      </form>
      <div className="space-y-3">
        {items.map((d) => (
          <div key={d.id} className="sr-card p-4 flex justify-between gap-3">
            <div className="flex-1">
              <p className="font-display text-[var(--sr-fire-2)]">{d.title} <span className="text-xs" style={{ color: "var(--sr-muted)" }}>#{d.order}</span></p>
              <p className="text-sm mt-1 whitespace-pre-wrap" style={{ color: "var(--sr-cream-soft)" }}>{d.body.slice(0, 200)}{d.body.length > 200 ? "..." : ""}</p>
            </div>
            <div className="flex flex-col gap-2">
              <button className="btn-ghost text-xs" onClick={() => edit(d)} data-testid={`edit-doc-${d.id}`}>Edytuj</button>
              <button className="text-[var(--sr-muted)] hover:text-red-400" onClick={() => remove(d.id)}><Trash2 size={16}/></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HistoryTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", date: "", date_mode: "year", year: "" });
  const [editingId, setEditingId] = useState(null);
  const load = () => api.get("/history").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const computeDate = () => {
    if (form.date_mode === "year") return form.year.trim();
    if (form.date_mode === "full" && form.date) return new Date(form.date).toISOString();
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    const dateValue = computeDate();
    if (!dateValue) { alert("Podaj rok lub pełną datę"); return; }
    const payload = { title: form.title, description: form.description, date: dateValue };
    if (editingId) {
      await api.put(`/history/${editingId}`, payload);
      setEditingId(null);
    } else {
      await api.post("/history", payload);
    }
    setForm({ title: "", description: "", date: "", date_mode: "year", year: "" });
    load();
  };

  const edit = (h) => {
    setEditingId(h.id);
    const v = String(h.date || "").trim();
    if (/^\d{4}$/.test(v)) {
      setForm({ title: h.title, description: h.description, date: "", date_mode: "year", year: v });
    } else {
      const isoDate = v ? v.slice(0, 10) : "";
      setForm({ title: h.title, description: h.description, date: isoDate, date_mode: "full", year: "" });
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({ title: "", description: "", date: "", date_mode: "year", year: "" });
  };

  const remove = async (id) => { if (window.confirm("Usunąć wpis?")) { await api.delete(`/history/${id}`); load(); } };

  const fmtDate = (v) => {
    const s = String(v || "").trim();
    if (!s) return "";
    if (/^\d{4}$/.test(s)) return s;
    const d = new Date(s);
    return isNaN(d.getTime()) ? s : d.toLocaleDateString("pl-PL", { day: "2-digit", month: "long", year: "numeric" });
  };

  return (
    <div data-testid="admin-history">
      <form onSubmit={submit} className="sr-card p-5 mb-5 grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="sr-label">Tytuł</label>
          <input className="sr-input" required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="np. Założenie klubu" data-testid="new-history-title" />
        </div>
        <div className="sm:col-span-2">
          <label className="sr-label">Format daty</label>
          <div className="flex gap-4 mt-1">
            <label className="flex items-center gap-2 text-sm" style={{ color: "var(--sr-cream-soft)" }}>
              <input type="radio" name="date_mode" value="year" checked={form.date_mode === "year"} onChange={() => setForm({...form, date_mode: "year"})} data-testid="history-mode-year" />
              Tylko rok (np. 2024)
            </label>
            <label className="flex items-center gap-2 text-sm" style={{ color: "var(--sr-cream-soft)" }}>
              <input type="radio" name="date_mode" value="full" checked={form.date_mode === "full"} onChange={() => setForm({...form, date_mode: "full"})} data-testid="history-mode-full" />
              Pełna data (dzień/miesiąc/rok)
            </label>
          </div>
        </div>
        {form.date_mode === "year" ? (
          <div>
            <label className="sr-label">Rok</label>
            <input className="sr-input" type="number" min="1900" max="2100" required value={form.year} onChange={(e) => setForm({...form, year: e.target.value})} placeholder="np. 2024" data-testid="new-history-year" />
          </div>
        ) : (
          <div>
            <label className="sr-label">Data</label>
            <input className="sr-input" type="date" required value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} data-testid="new-history-date" />
          </div>
        )}
        <div className="sm:col-span-2">
          <label className="sr-label">Opis</label>
          <textarea className="sr-input" rows={3} required value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Co się wydarzyło?" data-testid="new-history-desc" />
        </div>
        <div className="sm:col-span-2 flex gap-2">
          <button type="submit" className="btn-fire" data-testid="save-history-btn">
            {editingId ? "Zapisz zmiany" : "Dodaj wpis"}
          </button>
          {editingId && <button type="button" className="btn-ghost" onClick={cancelEdit}>Anuluj</button>}
        </div>
      </form>
      <div className="space-y-3">
        {items.map((h) => (
          <div key={h.id} className="sr-card p-4 flex justify-between gap-3">
            <div className="flex-1">
              <p className="font-display text-[var(--sr-fire-2)]">{h.title}</p>
              <p className="text-xs font-nav uppercase tracking-widest" style={{ color: "var(--sr-muted)" }}>
                {fmtDate(h.date)}
              </p>
              <p className="text-sm mt-2 whitespace-pre-wrap" style={{ color: "var(--sr-cream-soft)" }}>{h.description}</p>
            </div>
            <div className="flex flex-col gap-2">
              <button className="btn-ghost text-xs" onClick={() => edit(h)} data-testid={`edit-history-${h.id}`}>Edytuj</button>
              <button className="text-[var(--sr-muted)] hover:text-red-400" onClick={() => remove(h.id)}><Trash2 size={16}/></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MessagesTab() {
  const [msgs, setMsgs] = useState([]);
  useEffect(() => { api.get("/contact").then((r) => setMsgs(r.data)).catch(() => {}); }, []);
  return (
    <div data-testid="admin-messages">
      <div className="space-y-3">
        {msgs.map((m) => (
          <div key={m.id} className="sr-card p-4">
            <p className="font-display text-[var(--sr-fire-2)]">{m.name} <span className="text-sm" style={{ color: "var(--sr-muted)" }}>&lt;{m.email}&gt;</span></p>
            <p className="text-xs font-nav uppercase tracking-widest mt-1" style={{ color: "var(--sr-muted)" }}>{new Date(m.created_at).toLocaleString("pl-PL")}</p>
            <p className="mt-2 text-sm whitespace-pre-wrap" style={{ color: "var(--sr-cream-soft)" }}>{m.message}</p>
          </div>
        ))}
        {msgs.length === 0 && <p style={{ color: "var(--sr-muted)" }}>Brak wiadomości.</p>}
      </div>
    </div>
  );
}


const VISIBILITY_OPTIONS = [
  { value: "all", label: "Widoczna dla wszystkich" },
  { value: "members", label: "Tylko dla zalogowanych" },
  { value: "hidden", label: "Ukryta" },
];

const SETTINGS_FIELDS = [
  { key: "stats_rallies", label: "Statystyka — liczba zlotów i wypraw", type: "text", placeholder: "np. 48" },
  { key: "stats_km", label: "Statystyka — wspólnych km", type: "text", placeholder: "np. 120k" },
  { key: "about_intro", label: "Tekst „O klubie” — skrócony (strona główna)", type: "textarea", placeholder: "Akapity oddziel pustą linią" },
  { key: "about_long", label: "Tekst „O klubie” — pełny (podstrona O klubie)", type: "textarea", placeholder: "Akapity oddziel pustą linią" },
  { key: "expectations_list", label: "Tekst „Czego oczekujemy” — każda linia = osobny punkt", type: "textarea", placeholder: "Jedna linia = jeden wypunktowany wymóg" },
  { key: "contact_email", label: "Kontakt — e-mail", type: "text", placeholder: "np. kontakt@klub.pl" },
  { key: "contact_phone", label: "Kontakt — telefon", type: "text", placeholder: "np. +48 123 456 789" },
  { key: "contact_address", label: "Kontakt — adres", type: "text", placeholder: "np. Kocmyrzów-Luborzyca" },
  { key: "fb_url", label: "Link do Facebooka", type: "text", placeholder: "https://www.facebook.com/..." },
  { key: "prm_url", label: "Link do strony PRM", type: "text", placeholder: "https://prm1978.pl/" },
  { key: "map_query", label: "Mapa Google na stronie Kontakt — wpisz miejscowość lub adres", type: "text", placeholder: "np. Kocmyrzów-Luborzyca, Polska" },
  { key: "countdown_visibility", label: "Licznik do najbliższego wydarzenia — kto widzi", type: "visibility" },
  { key: "partners_visibility", label: "Zakładka „Nasi znajomi” — kto widzi", type: "visibility" },
  { key: "in_memory_visibility", label: "Zakładka „In Memory” — kto widzi", type: "visibility" },
];

function BackupScheduleCard({ values, setValues, saveOne, savingKey, savedKey }) {
  const [history, setHistory] = useState([]);
  const [running, setRunning] = useState("");
  const [runResult, setRunResult] = useState(null);

  const loadHistory = () => api.get("/backup/history").then((r) => setHistory(r.data || [])).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const setField = (k, v) => setValues((s) => ({ ...s, [k]: v }));
  const enabled = values.backup_enabled === "1";
  const b2Enabled = values.backup_b2_enabled === "1";

  const runNow = async (kind) => {
    if (!enabled) { alert("Najpierw włącz automatyczne kopie zapasowe."); return; }
    setRunning(kind);
    setRunResult(null);
    try {
      const { data } = await api.post(`/backup/run?kind=${kind}`);
      setRunResult(data);
      loadHistory();
    } catch (err) {
      setRunResult({ ok: false, error: err.response?.data?.detail || err.message });
    } finally {
      setRunning("");
    }
  };

  const fmtBytes = (n) => {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  };

  return (
    <div className="sr-card p-5" data-testid="backup-schedule-card" style={{ borderLeft: "3px solid var(--sr-fire)" }}>
      <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
        <h3 className="font-display text-xl text-[var(--sr-cream)]">Automatyczne kopie zapasowe</h3>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={async (e) => {
              const v = e.target.checked ? "1" : "0";
              setField("backup_enabled", v);
              await api.put("/settings/backup_enabled", { value: v });
            }}
            data-testid="backup-enabled-toggle"
          />
          <span className="font-nav text-xs uppercase tracking-widest" style={{ color: enabled ? "var(--sr-fire)" : "var(--sr-muted)" }}>
            {enabled ? "Włączone" : "Wyłączone"}
          </span>
        </label>
      </div>
      <p className="text-xs mb-4" style={{ color: "var(--sr-muted)" }}>
        <b>Hybryda:</b> codziennie o 3:00 archiwum lokalnie na serwerze (ostatnie N sztuk),
        w niedziele o 3:30 dodatkowo wypchnięcie na Backblaze B2.
        Po każdym backupie mail z raportem przez Resend.
        Archiwum zawiera dump bazy <b>+ wszystkie wgrane zdjęcia i filmy</b>.
      </p>

      <fieldset disabled={!enabled} className={enabled ? "" : "opacity-60 pointer-events-none"}>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="sr-label">Email do raportów</label>
            <input className="sr-input" type="email" placeholder="admin@scytheriders.pl"
              value={values.backup_email || ""}
              onChange={(e) => setField("backup_email", e.target.value)}
              onBlur={() => saveOne("backup_email")}
              data-testid="backup-email-input"
            />
            {savedKey === "backup_email" && <p className="text-xs mt-1" style={{ color: "#86efac" }}>Zapisano</p>}
          </div>
          <div>
            <label className="sr-label">Nadawca (Resend „from")</label>
            <input className="sr-input" placeholder="onboarding@resend.dev"
              value={values.backup_from_email || ""}
              onChange={(e) => setField("backup_from_email", e.target.value)}
              onBlur={() => saveOne("backup_from_email")}
            />
            <p className="text-xs mt-1" style={{ color: "var(--sr-muted)" }}>
              Na starcie wystarczy <code>onboarding@resend.dev</code>. Po podpięciu własnej domeny w Resend — wpisz np. <code>backup@twoja-domena.pl</code>.
            </p>
          </div>
          <div className="md:col-span-2">
            <label className="sr-label">Resend API key <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="underline">(gdzie go znaleźć)</a></label>
            <input className="sr-input" type="password" placeholder="re_..."
              value={values.backup_resend_key || ""}
              onChange={(e) => setField("backup_resend_key", e.target.value)}
              onBlur={() => saveOne("backup_resend_key")}
              data-testid="backup-resend-key"
            />
          </div>
          <div>
            <label className="sr-label">Ile lokalnych kopii trzymać</label>
            <input className="sr-input" type="number" min="1" max="30" style={{ width: 120 }}
              value={values.backup_local_keep || "4"}
              onChange={(e) => setField("backup_local_keep", e.target.value)}
              onBlur={() => saveOne("backup_local_keep")}
            />
            <p className="text-xs mt-1" style={{ color: "var(--sr-muted)" }}>Starsze są kasowane automatycznie.</p>
          </div>
        </div>

        <div className="sr-card p-4 mb-4" style={{ background: "rgba(255,106,26,0.04)" }}>
          <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
            <h4 className="font-display text-[var(--sr-cream)]">Backblaze B2 (zewnętrzny storage)</h4>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={b2Enabled}
                onChange={async (e) => {
                  const v = e.target.checked ? "1" : "0";
                  setField("backup_b2_enabled", v);
                  await api.put("/settings/backup_b2_enabled", { value: v });
                }}
                data-testid="backup-b2-toggle"
              />
              <span className="text-xs font-nav uppercase tracking-widest" style={{ color: b2Enabled ? "var(--sr-fire)" : "var(--sr-muted)" }}>
                {b2Enabled ? "Włączone" : "Wyłączone"}
              </span>
            </label>
          </div>
          <p className="text-xs mb-3" style={{ color: "var(--sr-muted)" }}>
            Jeśli wyłączone, robione są tylko kopie lokalne. Backblaze B2 to najtańszy storage chmurowy (~2 zł/mc za 10 GB). Załóż konto na <a href="https://www.backblaze.com/b2/cloud-storage.html" target="_blank" rel="noreferrer" className="underline">backblaze.com/b2</a>, utwórz bucket i klucz aplikacji.
          </p>
          <fieldset disabled={!b2Enabled} className={b2Enabled ? "grid md:grid-cols-3 gap-3" : "grid md:grid-cols-3 gap-3 opacity-60 pointer-events-none"}>
            <div>
              <label className="sr-label">Key ID</label>
              <input className="sr-input" value={values.backup_b2_key_id || ""}
                onChange={(e) => setField("backup_b2_key_id", e.target.value)}
                onBlur={() => saveOne("backup_b2_key_id")} />
            </div>
            <div>
              <label className="sr-label">Application Key</label>
              <input className="sr-input" type="password" value={values.backup_b2_app_key || ""}
                onChange={(e) => setField("backup_b2_app_key", e.target.value)}
                onBlur={() => saveOne("backup_b2_app_key")} />
            </div>
            <div>
              <label className="sr-label">Nazwa bucketa</label>
              <input className="sr-input" value={values.backup_b2_bucket || ""}
                onChange={(e) => setField("backup_b2_bucket", e.target.value)}
                onBlur={() => saveOne("backup_b2_bucket")} />
            </div>
          </fieldset>
        </div>

        <div className="flex gap-2 flex-wrap items-center">
          <button className="btn-solid" type="button" onClick={() => runNow("daily")} disabled={!!running} data-testid="backup-run-daily">
            {running === "daily" ? "Trwa…" : "Wyślij teraz (daily)"}
          </button>
          <button className="btn-fire" type="button" onClick={() => runNow("weekly")} disabled={!!running} data-testid="backup-run-weekly">
            {running === "weekly" ? "Trwa…" : "Wyślij teraz (weekly + B2)"}
          </button>
          <span className="text-xs" style={{ color: "var(--sr-muted)" }}>
            Harmonogram: codziennie 03:00 (daily), niedziela 03:30 (weekly).
          </span>
        </div>
        {runResult && (
          <div className="mt-3 text-xs p-3 rounded" style={{
            background: runResult.ok ? "rgba(134,239,172,0.08)" : "rgba(252,165,165,0.08)",
            border: "1px solid " + (runResult.ok ? "rgba(134,239,172,0.3)" : "rgba(252,165,165,0.3)"),
            color: runResult.ok ? "#86efac" : "#fca5a5",
          }} data-testid="backup-run-result">
            {runResult.ok
              ? <>✔️ Backup wykonany. Rozmiar: {fmtBytes(runResult.size_bytes)}. {runResult.email_sent ? "Email wysłany. " : ""}{runResult.b2_file ? `B2: ${runResult.b2_file}.` : ""}{runResult.error ? ` ⚠️ ${runResult.error}` : ""}</>
              : <>✖️ Błąd: {runResult.error || "nieznany"}</>
            }
          </div>
        )}
      </fieldset>

      {history.length > 0 && (
        <div className="mt-5">
          <h4 className="font-display text-[var(--sr-cream)] mb-2">Historia ({history.length})</h4>
          <div className="text-xs overflow-x-auto">
            <table className="sr-table">
              <thead><tr><th>Data</th><th>Typ</th><th>Status</th><th>Rozmiar</th><th>B2</th><th>Mail</th></tr></thead>
              <tbody>
                {history.slice(0, 10).map((h, i) => (
                  <tr key={i}>
                    <td>{(h.started_at || "").replace("T", " ").split(".")[0]}</td>
                    <td>{h.kind}</td>
                    <td style={{ color: h.ok ? "#86efac" : "#fca5a5" }}>{h.ok ? "OK" : "ERR"}</td>
                    <td>{fmtBytes(h.size_bytes)}</td>
                    <td>{h.b2_file ? "✔" : "—"}</td>
                    <td>{h.email_sent ? "✔" : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SettingsTab() {
  const [values, setValues] = useState({});
  const [savingKey, setSavingKey] = useState("");
  const [savedKey, setSavedKey] = useState("");
  const [uploading, setUploading] = useState(false);
  const [albums, setAlbums] = useState([]);

  useEffect(() => {
    api.get("/settings").then((r) => setValues(r.data || {})).catch(() => {});
    api.get("/albums").then((r) => setAlbums(r.data || [])).catch(() => {});
  }, []);

  const saveOne = async (key) => {
    setSavingKey(key);
    try {
      await api.put(`/settings/${key}`, { value: values[key] ?? "" });
      setSavedKey(key);
      refreshSettings();
      setTimeout(() => setSavedKey(""), 1800);
    } catch (err) {
      alert("Błąd zapisu: " + (err.response?.data?.detail || err.message));
    } finally {
      setSavingKey("");
    }
  };

  const saveAll = async () => {
    setSavingKey("__all__");
    try {
      await Promise.all(
        SETTINGS_FIELDS.map((f) => api.put(`/settings/${f.key}`, { value: values[f.key] ?? "" }))
      );
      setSavedKey("__all__");
      refreshSettings();
      setTimeout(() => setSavedKey(""), 1800);
    } catch (err) {
      alert("Błąd zapisu: " + (err.response?.data?.detail || err.message));
    } finally {
      setSavingKey("");
    }
  };

  const onPrmLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/settings/prm_logo_url", { value: data.url });
      setValues((v) => ({ ...v, prm_logo_url: data.url }));
      refreshSettings();
      setSavedKey("prm_logo_url");
      setTimeout(() => setSavedKey(""), 1800);
    } catch (err) {
      alert("Błąd uploadu logo: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const clearPrmLogo = async () => {
    if (!window.confirm("Usunąć obrazek logo PRM?")) return;
    await api.put("/settings/prm_logo_url", { value: "" });
    setValues((v) => ({ ...v, prm_logo_url: "" }));
    refreshSettings();
  };

  return (
    <div data-testid="admin-settings" className="space-y-4">
      <div className="sr-card p-5">
        <h3 className="font-display text-xl text-[var(--sr-cream)] mb-2">Kopia zapasowa bazy</h3>
        <p className="text-xs mb-4" style={{ color: "var(--sr-muted)" }}>
          Pobierz plik JSON z całą zawartością strony (członkowie, zdjęcia, wydarzenia, ustawienia). Zapisz go w bezpiecznym miejscu.
        </p>
        <button
          type="button"
          className="btn-fire"
          onClick={async () => {
            try {
              const res = await api.get("/backup", { responseType: "blob" });
              const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
              const a = document.createElement("a");
              a.href = url;
              const stamp = new Date().toISOString().replace(/[:T]/g, "-").split(".")[0];
              a.download = `scythe-riders-backup-${stamp}.json`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(url);
            } catch (err) {
              alert("Błąd pobierania kopii: " + (err.response?.data?.detail || err.message));
            }
          }}
          data-testid="settings-backup-btn"
        >
          Pobierz kopię zapasową
        </button>
      </div>

      <BackupScheduleCard values={values} setValues={setValues} saveOne={saveOne} savingKey={savingKey} savedKey={savedKey} />

      <div className="sr-card p-5">
        <h3 className="font-display text-xl text-[var(--sr-cream)] mb-2">Zdjęcie obok tekstu „O klubie" (strona główna)</h3>
        <p className="text-xs mb-4" style={{ color: "var(--sr-muted)" }}>
          Pojawia się obok skróconego tekstu o klubie na stronie głównej. Jeśli puste — wyświetli się szary placeholder.
        </p>
        <div className="flex items-center gap-5 flex-wrap">
          {values.home_about_image ? (
            <img
              src={resolveAssetUrl(values.home_about_image)}
              alt="Zdjęcie O klubie"
              style={{ width: 160, height: 100, objectFit: "cover", border: "1px solid var(--sr-border)" }}
              data-testid="settings-home-about-preview"
            />
          ) : (
            <div
              className="flex items-center justify-center font-nav text-[10px] uppercase tracking-widest text-center"
              style={{ width: 160, height: 100, border: "1px dashed var(--sr-border)", color: "var(--sr-muted)" }}
            >
              Brak zdjęcia
            </div>
          )}
          <div className="flex-1 min-w-[220px]">
            <input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                try {
                  const fd = new FormData();
                  fd.append("file", file);
                  const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
                  await api.put("/settings/home_about_image", { value: data.url });
                  setValues((v) => ({ ...v, home_about_image: data.url }));
                  refreshSettings();
                  setSavedKey("home_about_image");
                  setTimeout(() => setSavedKey(""), 1800);
                } catch (err) {
                  alert("Błąd uploadu: " + (err.response?.data?.detail || err.message));
                } finally {
                  e.target.value = "";
                }
              }}
              className="sr-input"
              data-testid="settings-home-about-upload"
            />
            {savedKey === "home_about_image" && <p className="text-xs mt-2" style={{ color: "#86efac" }}>Zapisano.</p>}
            {values.home_about_image && (
              <button
                type="button"
                className="btn-ghost mt-3 text-xs"
                onClick={async () => {
                  if (!window.confirm("Usunąć zdjęcie?")) return;
                  await api.put("/settings/home_about_image", { value: "" });
                  setValues((v) => ({ ...v, home_about_image: "" }));
                  refreshSettings();
                }}
                data-testid="settings-home-about-clear"
              >
                Usuń zdjęcie
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="sr-card p-5">
        <h3 className="font-display text-xl text-[var(--sr-cream)] mb-2">Galeria na stronie głównej — skąd brać zdjęcia?</h3>
        <p className="text-xs mb-4" style={{ color: "var(--sr-muted)" }}>
          Pokazuje 4 najnowsze zdjęcia z wybranego albumu na samym dole strony głównej.
        </p>
        <select
          className="sr-input"
          value={values.home_gallery_album ?? ""}
          onChange={async (e) => {
            const v = e.target.value;
            setValues((prev) => ({ ...prev, home_gallery_album: v }));
            await api.put("/settings/home_gallery_album", { value: v });
            refreshSettings();
            setSavedKey("home_gallery_album");
            setTimeout(() => setSavedKey(""), 1800);
          }}
          data-testid="settings-home-gallery-album"
        >
          <option value="">— Automatycznie (najnowsze publiczne zdjęcia) —</option>
          {albums.map((a) => (
            <option key={a.id} value={a.id}>
              {a.title} ({a.category}, {a.photo_count} zdjęć)
            </option>
          ))}
          <option value="__hide__">— Ukryj sekcję galerii na stronie głównej —</option>
        </select>
        {savedKey === "home_gallery_album" && <p className="text-xs mt-2" style={{ color: "#86efac" }}>Zapisano.</p>}
      </div>

      <div className="sr-card p-5">
        <h3 className="font-display text-xl text-[var(--sr-cream)] mb-2">Logo PRM (banner + stopka)</h3>
        <p className="text-xs mb-4" style={{ color: "var(--sr-muted)" }}>
          Jeżeli logo nie zostanie wgrane — baner PRM nie wyświetli obrazka (pokaże tylko tekst).
        </p>
        <div className="flex items-center gap-5 flex-wrap">
          {values.prm_logo_url ? (
            <img
              src={resolveAssetUrl(values.prm_logo_url)}
              alt="PRM logo"
              style={{ width: 90, height: 90, objectFit: "contain", border: "1px solid var(--sr-border)", background: "rgba(0,0,0,0.3)", padding: 6 }}
              data-testid="settings-prm-preview"
            />
          ) : (
            <div
              className="flex items-center justify-center font-nav text-[10px] uppercase tracking-widest text-center"
              style={{ width: 90, height: 90, border: "1px dashed var(--sr-border)", color: "var(--sr-muted)" }}
            >
              Brak logo
            </div>
          )}
          <div className="flex-1 min-w-[220px]">
            <input
              type="file"
              accept="image/*"
              onChange={onPrmLogoFile}
              className="sr-input"
              data-testid="settings-prm-upload"
            />
            {uploading && <p className="text-xs mt-2" style={{ color: "var(--sr-fire)" }}>Wgrywanie...</p>}
            {savedKey === "prm_logo_url" && <p className="text-xs mt-2" style={{ color: "#86efac" }}>Zapisano.</p>}
            {values.prm_logo_url && (
              <button type="button" className="btn-ghost mt-3 text-xs" onClick={clearPrmLogo} data-testid="settings-prm-clear">
                Usuń logo
              </button>
            )}
          </div>
        </div>
      </div>

      {SETTINGS_FIELDS.map((f) => (
        <div key={f.key} className="sr-card p-5">
          <label className="sr-label">{f.label}</label>
          {f.type === "textarea" ? (
            <textarea
              className="sr-input"
              rows={5}
              value={values[f.key] ?? ""}
              onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
              placeholder={f.placeholder}
              data-testid={`settings-${f.key}`}
            />
          ) : f.type === "visibility" ? (
            <select
              className="sr-input"
              value={values[f.key] ?? "all"}
              onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
              data-testid={`settings-${f.key}`}
            >
              {VISIBILITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          ) : (
            <input
              className="sr-input"
              value={values[f.key] ?? ""}
              onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
              placeholder={f.placeholder}
              data-testid={`settings-${f.key}`}
            />
          )}
          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              className="btn-ghost text-xs"
              disabled={savingKey === f.key}
              onClick={() => saveOne(f.key)}
              data-testid={`settings-save-${f.key}`}
            >
              {savingKey === f.key ? "Zapisuję..." : "Zapisz to pole"}
            </button>
            {savedKey === f.key && <span className="text-xs" style={{ color: "#86efac" }}>Zapisano.</span>}
          </div>
        </div>
      ))}

      <div className="flex items-center gap-3">
        <button
          type="button"
          className="btn-fire"
          disabled={savingKey === "__all__"}
          onClick={saveAll}
          data-testid="settings-save-all"
        >
          {savingKey === "__all__" ? "Zapisuję..." : "Zapisz wszystkie pola"}
        </button>
        {savedKey === "__all__" && <span className="text-sm" style={{ color: "#86efac" }}>Zapisano wszystko.</span>}
      </div>
    </div>
  );
}

// ============== PARTNERS ==============
function PartnersTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", url: "", description: "", logo_url: "", order: 0 });
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const load = () => api.get("/partners").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.url.trim()) { alert("Nazwa i link są wymagane"); return; }
    if (editingId) await api.put(`/partners/${editingId}`, form);
    else await api.post("/partners", form);
    setForm({ name: "", url: "", description: "", logo_url: "", order: 0 });
    setEditingId(null);
    load();
  };

  const edit = (p) => { setEditingId(p.id); setForm({ name: p.name, url: p.url, description: p.description || "", logo_url: p.logo_url || "", order: p.order || 0 }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const cancel = () => { setEditingId(null); setForm({ name: "", url: "", description: "", logo_url: "", order: 0 }); };
  const remove = async (id) => { if (window.confirm("Usunąć wpis?")) { await api.delete(`/partners/${id}`); load(); } };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_url: data.url }));
    } catch (err) {
      alert("Błąd uploadu: " + (err.response?.data?.detail || err.message));
    } finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <div data-testid="admin-partners">
      <form onSubmit={submit} className="sr-card p-5 mb-5 grid sm:grid-cols-2 gap-3">
        <div>
          <label className="sr-label">Nazwa</label>
          <input className="sr-input" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="np. MotoKrew" data-testid="partner-name" />
        </div>
        <div>
          <label className="sr-label">Link (URL)</label>
          <input className="sr-input" required value={form.url} onChange={(e) => setForm({...form, url: e.target.value})} placeholder="https://..." data-testid="partner-url" />
        </div>
        <div className="sm:col-span-2">
          <label className="sr-label">Opis (opcjonalny)</label>
          <input className="sr-input" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Krótki opis" />
        </div>
        <div className="sm:col-span-2 flex gap-3 items-end flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <label className="sr-label">Logo (opcjonalne)</label>
            <input type="file" accept="image/*" onChange={uploadLogo} className="sr-input" data-testid="partner-logo-file" />
            {uploading && <p className="text-xs mt-1" style={{ color: "var(--sr-fire)" }}>Wgrywanie...</p>}
          </div>
          {form.logo_url && (
            <img src={form.logo_url.startsWith("http") ? form.logo_url : `${process.env.REACT_APP_BACKEND_URL}${form.logo_url}`} alt="" style={{ width: 64, height: 64, objectFit: "contain", border: "1px solid var(--sr-border)" }} />
          )}
          <div>
            <label className="sr-label">Kolejność</label>
            <input className="sr-input" type="number" value={form.order} onChange={(e) => setForm({...form, order: Number(e.target.value) || 0})} style={{ width: 80 }} />
          </div>
        </div>
        <div className="sm:col-span-2 flex gap-2">
          <button type="submit" className="btn-fire" data-testid="partner-save-btn">{editingId ? "Zapisz zmiany" : "Dodaj"}</button>
          {editingId && <button type="button" className="btn-ghost" onClick={cancel}>Anuluj</button>}
        </div>
      </form>

      <div className="space-y-2">
        {items.map((p) => (
          <div key={p.id} className="sr-card p-3 flex gap-3 items-center">
            {p.logo_url && <img src={p.logo_url.startsWith("http") ? p.logo_url : `${process.env.REACT_APP_BACKEND_URL}${p.logo_url}`} alt="" style={{ width: 48, height: 48, objectFit: "contain" }} />}
            <div className="flex-1 min-w-0">
              <p className="font-display text-[var(--sr-fire-2)] truncate">{p.name}</p>
              <p className="text-xs" style={{ color: "var(--sr-muted)" }}>{p.url}</p>
            </div>
            <button className="btn-ghost text-xs" onClick={() => edit(p)}>Edytuj</button>
            <button className="text-[var(--sr-muted)] hover:text-red-400" onClick={() => remove(p.id)}><Trash2 size={16}/></button>
          </div>
        ))}
        {items.length === 0 && <p style={{ color: "var(--sr-muted)" }}>Brak wpisów.</p>}
      </div>
    </div>
  );
}


// ============== IN MEMORY ==============
function InMemoryTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", image_url: "", date_from: "", date_to: "", order: 0 });
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const load = () => api.get("/in-memory").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { alert("Imię jest wymagane"); return; }
    if (editingId) await api.put(`/in-memory/${editingId}`, form);
    else await api.post("/in-memory", form);
    setForm({ name: "", description: "", image_url: "", date_from: "", date_to: "", order: 0 });
    setEditingId(null);
    load();
  };
  const edit = (m) => { setEditingId(m.id); setForm({ name: m.name, description: m.description || "", image_url: m.image_url || "", date_from: m.date_from || "", date_to: m.date_to || "", order: m.order || 0 }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const cancel = () => { setEditingId(null); setForm({ name: "", description: "", image_url: "", date_from: "", date_to: "", order: 0 }); };
  const remove = async (id) => { if (window.confirm("Usunąć wpis?")) { await api.delete(`/in-memory/${id}`); load(); } };

  const uploadImg = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.url }));
    } catch (err) { alert("Błąd uploadu: " + (err.response?.data?.detail || err.message)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <div data-testid="admin-in-memory">
      <form onSubmit={submit} className="sr-card p-5 mb-5 grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="sr-label">Imię i nazwisko / pseudonim</label>
          <input className="sr-input" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="inmemory-name" />
        </div>
        <div>
          <label className="sr-label">Data od (opcjonalna, np. 1965)</label>
          <input className="sr-input" value={form.date_from} onChange={(e) => setForm({...form, date_from: e.target.value})} placeholder="np. 1965" />
        </div>
        <div>
          <label className="sr-label">Data do (opcjonalna, np. 2024)</label>
          <input className="sr-input" value={form.date_to} onChange={(e) => setForm({...form, date_to: e.target.value})} placeholder="np. 2024" />
        </div>
        <div className="sm:col-span-2">
          <label className="sr-label">Wspomnienie (opcjonalne)</label>
          <textarea className="sr-input" rows={4} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Krótkie wspomnienie..." />
        </div>
        <div className="sm:col-span-2 flex gap-3 items-end flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <label className="sr-label">Zdjęcie (opcjonalne)</label>
            <input type="file" accept="image/*" onChange={uploadImg} className="sr-input" />
            {uploading && <p className="text-xs mt-1" style={{ color: "var(--sr-fire)" }}>Wgrywanie...</p>}
          </div>
          {form.image_url && (
            <img src={form.image_url.startsWith("http") ? form.image_url : `${process.env.REACT_APP_BACKEND_URL}${form.image_url}`} alt="" style={{ width: 72, height: 96, objectFit: "cover", border: "1px solid var(--sr-border)" }} />
          )}
        </div>
        <div className="sm:col-span-2 flex gap-2">
          <button type="submit" className="btn-fire">{editingId ? "Zapisz zmiany" : "Dodaj"}</button>
          {editingId && <button type="button" className="btn-ghost" onClick={cancel}>Anuluj</button>}
        </div>
      </form>

      <div className="space-y-2">
        {items.map((m) => (
          <div key={m.id} className="sr-card p-3 flex gap-3 items-center">
            {m.image_url && <img src={m.image_url.startsWith("http") ? m.image_url : `${process.env.REACT_APP_BACKEND_URL}${m.image_url}`} alt="" style={{ width: 48, height: 64, objectFit: "cover" }} />}
            <div className="flex-1 min-w-0">
              <p className="font-display text-[var(--sr-fire-2)] truncate">{m.name}</p>
              <p className="text-xs" style={{ color: "var(--sr-muted)" }}>{m.date_from} {m.date_from && m.date_to && "—"} {m.date_to}</p>
            </div>
            <button className="btn-ghost text-xs" onClick={() => edit(m)}>Edytuj</button>
            <button className="text-[var(--sr-muted)] hover:text-red-400" onClick={() => remove(m.id)}><Trash2 size={16}/></button>
          </div>
        ))}
        {items.length === 0 && <p style={{ color: "var(--sr-muted)" }}>Brak wpisów.</p>}
      </div>
    </div>
  );
}


// ============== VIDEOS ==============
function VideosTab() {
  const [items, setItems] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", is_public: true, album_id: "", video_url: "", video_type: "link" });
  const [uploading, setUploading] = useState(false);
  const load = () => api.get("/videos").then((r) => setItems(r.data || []));
  useEffect(() => { load(); api.get("/albums").then((r) => setAlbums(r.data || [])); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.video_url.trim()) { alert("Podaj link lub wgraj plik"); return; }
    await api.post("/videos", { ...form, album_id: form.album_id || null });
    setForm({ title: "", description: "", is_public: true, album_id: "", video_url: "", video_type: "link" });
    load();
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) { alert("Maksymalny rozmiar filmu to 200 MB"); e.target.value = ""; return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads/video", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 });
      setForm((f) => ({ ...f, video_url: data.url, video_type: "file" }));
    } catch (err) { alert("Błąd uploadu: " + (err.response?.data?.detail || err.message)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const remove = async (id) => { if (window.confirm("Usunąć film?")) { await api.delete(`/videos/${id}`); load(); } };

  return (
    <div data-testid="admin-videos">
      <form onSubmit={submit} className="sr-card p-5 mb-5 grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="sr-label">Tytuł (opcjonalny)</label>
          <input className="sr-input" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} />
        </div>
        <div>
          <label className="sr-label">Rodzaj</label>
          <select className="sr-input" value={form.video_type} onChange={(e) => setForm({...form, video_type: e.target.value, video_url: ""})}>
            <option value="link">Link (YouTube / Vimeo)</option>
            <option value="file">Plik (do 200 MB)</option>
          </select>
        </div>
        <div>
          <label className="sr-label">Album (opcjonalny)</label>
          <select className="sr-input" value={form.album_id} onChange={(e) => setForm({...form, album_id: e.target.value})}>
            <option value="">— Bez albumu —</option>
            {albums.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
          </select>
        </div>
        {form.video_type === "link" ? (
          <div className="sm:col-span-2">
            <label className="sr-label">Link do filmu (YouTube / Vimeo)</label>
            <input className="sr-input" required value={form.video_url} onChange={(e) => setForm({...form, video_url: e.target.value})} placeholder="https://www.youtube.com/watch?v=..." />
          </div>
        ) : (
          <div className="sm:col-span-2">
            <label className="sr-label">Plik video (max 200 MB)</label>
            <input type="file" accept="video/*" onChange={uploadFile} className="sr-input" />
            {uploading && <p className="text-xs mt-1" style={{ color: "var(--sr-fire)" }}>Wgrywanie może potrwać kilka minut...</p>}
            {form.video_url && <p className="text-xs mt-1" style={{ color: "#86efac" }}>Wgrane: {form.video_url}</p>}
          </div>
        )}
        <div className="sm:col-span-2 flex items-center gap-2">
          <input id="video_public" type="checkbox" checked={form.is_public} onChange={(e) => setForm({...form, is_public: e.target.checked})} />
          <label htmlFor="video_public" className="font-nav text-sm uppercase tracking-widest" style={{ color: "var(--sr-cream-soft)" }}>
            Publiczne (jeśli wybrałeś album, ta opcja zostanie nadpisana ustawieniem albumu)
          </label>
        </div>
        <div className="sm:col-span-2">
          <button type="submit" className="btn-fire">Dodaj film</button>
        </div>
      </form>

      <div className="grid sm:grid-cols-2 gap-3">
        {items.map((v) => (
          <div key={v.id} className="sr-card p-3">
            <p className="font-display text-[var(--sr-fire-2)]">{v.title || "(bez tytułu)"}</p>
            <p className="text-xs" style={{ color: "var(--sr-muted)" }}>
              {v.video_type === "file" ? "Plik" : "Link"} · {v.is_public ? "publiczny" : "klubowy"}
            </p>
            <a href={v.video_url.startsWith("http") ? v.video_url : `${process.env.REACT_APP_BACKEND_URL}${v.video_url}`} target="_blank" rel="noreferrer" className="text-xs text-[var(--sr-fire)] hover:underline break-all">{v.video_url}</a>
            <div className="mt-2"><button className="text-[var(--sr-muted)] hover:text-red-400" onClick={() => remove(v.id)}><Trash2 size={14}/></button></div>
          </div>
        ))}
        {items.length === 0 && <p style={{ color: "var(--sr-muted)" }}>Brak filmów.</p>}
      </div>
    </div>
  );
}


// ============== FORMATIONS (szyk jazdy) ==============
// Klasyczny „zakładka": 1 motocykl na rząd, naprzemiennie L / R. Nazwa obok miniaturki.
function FormationPreview({ items }) {
  return (
    <div
      className="p-4 rounded-md"
      style={{
        background: "linear-gradient(180deg, rgba(12,10,9,0.9), rgba(12,10,9,0.55))",
        border: "1px solid var(--sr-border)",
      }}
      data-testid="formation-preview"
    >
      <div className="flex flex-col gap-2 items-start mx-auto" style={{ width: "fit-content", minWidth: 200 }}>
        {items.map((it, i) => {
          const offset = i % 2 === 1;
          return (
            <div
              key={i}
              className="flex items-center gap-2"
              style={{ marginLeft: offset ? 50 : 0 }}
            >
              <div
                className="flex items-center justify-center rounded-full shrink-0"
                style={{
                  width: 32,
                  height: 32,
                  background: "rgba(255,106,26,0.12)",
                  border: "2px solid var(--sr-fire)",
                  boxShadow: "0 0 10px rgba(255,106,26,0.22)",
                }}
              >
                <Bike className="text-[var(--sr-fire)]" size={16} />
              </div>
              <div>
                <p className="font-display text-xs text-[var(--sr-cream)] leading-tight">
                  {it.name || "—"}
                </p>
                {it.motorcycle && (
                  <p className="text-[9px] font-nav uppercase tracking-widest" style={{ color: "var(--sr-muted)" }}>
                    {it.motorcycle}
                  </p>
                )}
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <p className="text-xs italic" style={{ color: "var(--sr-muted)" }}>
            Brak motocykli
          </p>
        )}
      </div>
    </div>
  );
}

function FormationsTab() {
  const [items, setItems] = useState([]);
  const [editor, setEditor] = useState(null); // current editing formation
  const load = () => api.get("/formations").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  // Convert backend formation -> editor shape. Older saves had row/col; we rebuild an
  // ordered flat list from them so the zigzag preview matches what was seen previously.
  const toEditor = (f) => {
    const raw = (f?.items || []).map((x) => ({
      name: x.name || "",
      motorcycle: x.motorcycle || "",
      _row: Number.isFinite(x.row) ? x.row : 0,
      _col: Number.isFinite(x.col) ? x.col : 0,
    }));
    // If any row/col data present, sort by (row, col); otherwise keep insertion order.
    const hasPos = raw.some((x) => x._row > 0 || x._col > 0);
    const sorted = hasPos
      ? [...raw].sort((a, b) => a._row - b._row || a._col - b._col)
      : raw;
    return {
      id: f?.id || null,
      title: f?.title || "",
      description: f?.description || "",
      items: sorted.map(({ name, motorcycle }) => ({ name, motorcycle })),
    };
  };

  const newFormation = () =>
    setEditor({ id: null, title: "", description: "", items: [{ name: "", motorcycle: "" }] });

  const save = async () => {
    const title = editor.title.trim();
    if (!title) { alert("Podaj nazwę kolumny"); return; }
    // Zawsze zapisujemy jako 1 motocykl na rząd — zygzak renderuje się naprzemiennie L/R.
    const items = editor.items.map((it, idx) => ({
      name: it.name || "",
      motorcycle: it.motorcycle || "",
      row: idx,
      col: 0,
    }));
    const payload = { title, description: editor.description || "", per_row: 1, items };
    if (editor.id) await api.put(`/formations/${editor.id}`, payload);
    else await api.post("/formations", payload);
    setEditor(null); load();
  };
  const remove = async (id) => {
    if (!window.confirm("Usunąć tę kolumnę?")) return;
    await api.delete(`/formations/${id}`); load();
  };

  const addSlot = () =>
    setEditor((e) => ({ ...e, items: [...e.items, { name: "", motorcycle: "" }] }));
  const removeSlot = (idx) =>
    setEditor((e) => ({ ...e, items: e.items.filter((_, i) => i !== idx) }));
  const updateSlot = (idx, patch) =>
    setEditor((e) => ({
      ...e,
      items: e.items.map((x, i) => (i === idx ? { ...x, ...patch } : x)),
    }));
  const moveSlot = (idx, delta) =>
    setEditor((e) => {
      const next = [...e.items];
      const j = idx + delta;
      if (j < 0 || j >= next.length) return e;
      [next[idx], next[j]] = [next[j], next[idx]];
      return { ...e, items: next };
    });

  if (editor) {
    return (
      <div data-testid="admin-formation-edit" className="space-y-4">
        <button className="btn-ghost" onClick={() => setEditor(null)}>← Wróć do listy</button>

        <div className="sr-card p-5 space-y-3">
          <div>
            <label className="sr-label">Nazwa kolumny</label>
            <input
              className="sr-input"
              value={editor.title}
              onChange={(e) => setEditor({ ...editor, title: e.target.value })}
              placeholder="np. Wyjazd Bieszczady 15.06"
              data-testid="formation-title"
            />
          </div>
          <div>
            <label className="sr-label">Opis (opcjonalny)</label>
            <input
              className="sr-input"
              value={editor.description}
              onChange={(e) => setEditor({ ...editor, description: e.target.value })}
            />
          </div>
          <div>
            <p className="text-xs" style={{ color: "var(--sr-muted)" }}>
              Szyk jedzie z góry na dół. Motocykle układają się naprzemiennie w zygzak
              (pierwszy po lewej, drugi po prawej, trzeci po lewej itd.).
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <div className="sr-card p-5">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-display text-lg text-[var(--sr-cream)]">Motocykle w szyku</h4>
              <button className="btn-solid" onClick={addSlot} data-testid="formation-add-slot">
                + Dodaj
              </button>
            </div>
            <div className="space-y-2">
              {editor.items.map((s, i) => (
                <div key={i} className="sr-card p-3" data-testid={`formation-slot-${i}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className="font-nav text-xs uppercase tracking-widest"
                      style={{ color: "var(--sr-muted)", minWidth: 32 }}
                    >
                      #{i + 1}
                    </span>
                    <button
                      type="button"
                      className="p-1 hover:text-[var(--sr-fire)] disabled:opacity-30"
                      onClick={() => moveSlot(i, -1)}
                      disabled={i === 0}
                      title="W górę"
                      data-testid={`formation-slot-up-${i}`}
                    >
                      <ArrowUp size={16} />
                    </button>
                    <button
                      type="button"
                      className="p-1 hover:text-[var(--sr-fire)] disabled:opacity-30"
                      onClick={() => moveSlot(i, 1)}
                      disabled={i === editor.items.length - 1}
                      title="W dół"
                      data-testid={`formation-slot-down-${i}`}
                    >
                      <ArrowDown size={16} />
                    </button>
                    <div className="flex-1" />
                    <button
                      type="button"
                      className="text-[var(--sr-muted)] hover:text-red-400"
                      onClick={() => removeSlot(i)}
                      title="Usuń"
                      data-testid={`formation-slot-remove-${i}`}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                  <label className="sr-label">Imię kierowcy</label>
                  <input
                    className="sr-input"
                    value={s.name}
                    onChange={(e) => updateSlot(i, { name: e.target.value })}
                  />
                  <label className="sr-label mt-2">Motocykl</label>
                  <input
                    className="sr-input"
                    value={s.motorcycle}
                    onChange={(e) => updateSlot(i, { motorcycle: e.target.value })}
                  />
                </div>
              ))}
              {editor.items.length === 0 && (
                <p style={{ color: "var(--sr-muted)" }}>Brak motocykli. Dodaj pierwszy.</p>
              )}
            </div>
          </div>

          <div className="sr-card p-5">
            <h4 className="font-display text-lg text-[var(--sr-cream)] mb-3">Podgląd</h4>
            <FormationPreview items={editor.items} />
          </div>
        </div>

        <button className="btn-fire" onClick={save} data-testid="formation-save">
          Zapisz kolumnę
        </button>
      </div>
    );
  }

  return (
    <div data-testid="admin-formations">
      <button className="btn-fire mb-4" onClick={newFormation}>+ Nowa kolumna</button>
      <div className="space-y-2">
        {items.map((f) => (
          <div key={f.id} className="sr-card p-3 flex gap-3 items-center">
            <div className="flex-1 min-w-0">
              <p className="font-display text-[var(--sr-fire-2)] truncate">{f.title}</p>
              <p className="text-xs" style={{ color: "var(--sr-muted)" }}>
                {(f.items || []).length} motocykli
              </p>
            </div>
            <button className="btn-ghost text-xs" onClick={() => setEditor(toEditor(f))}>
              Edytuj
            </button>
            <button className="text-[var(--sr-muted)] hover:text-red-400" onClick={() => remove(f.id)}>
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        {items.length === 0 && <p style={{ color: "var(--sr-muted)" }}>Brak kolumn. Utwórz pierwszą.</p>}
      </div>
    </div>
  );
}

